import React, { useState, useEffect } from 'react';
import { Holiday } from '../types';
import { getHolidaysApi, addHolidayApi, deleteHolidayApi } from '../api/holidayApi';
import { TrashIcon } from './icons/TrashIcon';
import { PlusIcon } from './icons/PlusIcon';

const Holidays: React.FC = () => {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newDate, setNewDate] = useState('');
  const [newDescription, setNewDescription] = useState('');

  useEffect(() => {
    loadHolidays();
  }, []);

  const loadHolidays = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getHolidaysApi();
      // Filter future holidays and sort by date
      const futureHolidays = data
        .filter(h => new Date(h.date) >= new Date())
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      setHolidays(futureHolidays);
    } catch (err) {
      setError('טעינת החגים נכשלה');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddHoliday = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDate) return;

    // Validate date is not in the past
    const selectedDate = new Date(newDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      setError('לא ניתן להוסיף תאריך בעבר');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const newHoliday = await addHolidayApi({
        date: newDate,
        description: newDescription || undefined,
      });
      setHolidays([...holidays, newHoliday].sort((a, b) => 
        new Date(a.date).getTime() - new Date(b.date).getTime()
      ));
      setNewDate('');
      setNewDescription('');
    } catch (err) {
      setError('הוספת החג נכשלה');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHoliday = async (id: string) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את החג הזה?')) return;

    try {
      setLoading(true);
      setError(null);
      await deleteHolidayApi(id);
      setHolidays(holidays.filter(h => h.id !== id));
    } catch (err) {
      setError('מחיקת החג נכשלה');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatHebrewDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const getTodayString = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-slate-800">ניהול חגים וסגירות</h2>
        <p className="text-slate-500 mt-1">הוסף ימים בהם המרפאה תהיה סגורה</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Add Holiday Form */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-6">
            <h3 className="text-lg font-bold text-slate-800 mb-4">הוסף חג חדש</h3>
            <form onSubmit={handleAddHoliday}>
              <div className="mb-4">
                <label htmlFor="holidayDate" className="block text-sm font-medium text-slate-700 mb-2">
                  תאריך
                </label>
                <input
                  type="date"
                  id="holidayDate"
                  value={newDate}
                  min={getTodayString()}
                  onChange={(e) => setNewDate(e.target.value)}
                  className="w-full p-2 border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label htmlFor="holidayDescription" className="block text-sm font-medium text-slate-700 mb-2">
                  תיאור (אופציונלי)
                </label>
                <input
                  type="text"
                  id="holidayDescription"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="למשל: חג המולד"
                  className="w-full p-2 border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !newDate}
                className="w-full flex items-center justify-center bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PlusIcon className="w-5 h-5 ml-2" />
                הוסף חג
              </button>
            </form>
          </div>
        </div>

        {/* Holidays List */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-bold text-slate-800 mb-4">
              חגים עתידיים ({holidays.length})
            </h3>

            {loading && holidays.length === 0 && (
              <div className="text-center p-8 text-slate-500">
                טוען...
              </div>
            )}

            {!loading && holidays.length === 0 && (
              <div className="text-center p-8 text-slate-500">
                <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p>עדיין לא נוספו חגים</p>
              </div>
            )}

            <div className="space-y-3">
              {holidays.map((holiday) => (
                <div
                  key={holiday.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">
                        {formatHebrewDate(holiday.date)}
                      </p>
                      {holiday.description && (
                        <p className="text-sm text-slate-600">{holiday.description}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteHoliday(holiday.id)}
                    disabled={loading}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="מחק חג"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 ml-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <h4 className="font-semibold text-blue-900 mb-1">הערה חשובה</h4>
            <p className="text-sm text-blue-700">בימי חג, המרפאה תהיה סגורה ולא ניתן יהיה לקבוע תורים. התורים הקיימים לא יושפעו אוטומטית.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Holidays;
